import type { ListingPage } from "@wags/catalog/listings/listing-page.contract.ts";
import { ProviderType } from "@wags/catalog/pure.ts";
import { useState, type ComponentProps, type ReactNode } from "react";
import { usePostHog } from "posthog-js/react";
import type { Review } from "@/components/shared/ReviewsSection";
import { BreedSection } from "@/features/breeds";
import {
    FaqSection,
    type FaqAskSource,
    hasPriceContent,
    listingFeeInclusions,
    type ListingData,
    MeetTheParents,
    PriceSection,
} from "@/features/listings";
import {
    requestListingQuestion,
    requestOpenChat,
    useListingQuestionReply,
} from "@/features/kits-conversations";
import {
    type Fact,
    ListingReviewsSection,
    type useProviderProfileV2,
} from "@/features/providers";
import {
    BreederSection,
    hasBreederContent,
    ProviderHighlights,
} from "./breeder-section";
import { ClaimEntry } from "./claim-entry";
import { LocationSection } from "./location-section";

type ParentProps = ComponentProps<typeof MeetTheParents>;

const FAQ_ASK_STALL_MS = 15_000;

type FaqAsk = {
    question: string;
    source: FaqAskSource;
    inputId: string;
    stalled: boolean;
};

export type DogPageSectionsProps = {
    listing: ListingData | null | undefined;
    dogId: string;
    dogName: string;
    copilotEnabled: boolean;
    providerType: ProviderType;
    providerAvatarUrl: string | undefined;
    providerProfile: ReturnType<typeof useProviderProfileV2>;
    listingPageV2: ListingPage | null | undefined;
    parentDogs: ParentProps["parentDogs"];
    sexAvailability: ParentProps["sexAvailability"];
    reviews: { total: number; reviews: Review[] } | null;
    organizationId: string | null;
    organizationLocationLabel: string;
    screeningFacts: Fact[] | undefined;
    providerCard: ReactNode;
};

function Price({
    listing,
    providerProfile,
    listingPageV2,
}: DogPageSectionsProps & { listing: ListingData }) {
    if (!hasPriceContent(listing)) return null;
    const inclusions = listingFeeInclusions({
        listingIncluded: listingPageV2?.application?.included,
        providerIncluded: providerProfile?.pricing.fees[0]?.includes,
    });
    return (
        <div>
            <PriceSection data={listing} includedInFee={inclusions.items} />
        </div>
    );
}

function Parents({
    providerType,
    parentDogs,
    dogName,
    sexAvailability,
}: DogPageSectionsProps) {
    if (providerType !== ProviderType.Breeder) return null;
    if (!parentDogs.mom && !parentDogs.dad) return null;
    return (
        <MeetTheParents
            parentDogs={parentDogs}
            dogName={dogName}
            sexAvailability={sexAvailability}
        />
    );
}

function ProviderBlock(props: DogPageSectionsProps & { listing: ListingData }) {
    const { listing, providerType, providerProfile, providerCard } = props;
    if (
        !providerCard &&
        !hasBreederContent(listing, providerProfile?.highlights)
    )
        return null;
    const isBreeder = providerType === ProviderType.Breeder;
    return (
        <div>
            <h2 className="text-section-title text-doberman-black">
                {isBreeder ? "Meet the breeder" : "Meet the shelter"}
            </h2>
            <div className="mt-4 rounded-card bg-true-white border-shadow p-card">
                {providerCard ? (
                    <>
                        {providerCard}
                        <ProviderHighlights
                            highlights={providerProfile?.highlights}
                        />
                    </>
                ) : (
                    <BreederSection
                        data={listing}
                        providerKind={isBreeder ? "breeder" : "shelter"}
                        avatarUrl={props.providerAvatarUrl}
                        reviewCount={props.reviews?.total ?? null}
                        rating={
                            providerProfile?.reputation.averageRating ?? null
                        }
                        ratingCount={
                            providerProfile?.reputation.ratingCount ?? null
                        }
                        highlights={providerProfile?.highlights}
                    />
                )}
                <ClaimEntry
                    providerId={listing.providerId}
                    providerName={listing.providerName}
                    listingId={props.dogId}
                    providerType={providerType}
                    enabled={props.copilotEnabled}
                    isVerified={listing.providerIsVerified ?? false}
                    locationLabel={props.organizationLocationLabel}
                    surface="attached"
                />
            </div>
        </div>
    );
}

function Reviews({
    reviews,
    organizationId,
    providerCard,
}: DogPageSectionsProps) {
    if (!reviews || providerCard) return null;
    return (
        <ListingReviewsSection
            reviewCount={reviews.total}
            reviews={reviews.reviews}
            organizationId={organizationId ?? undefined}
        />
    );
}

function Location({
    listing,
    copilotEnabled,
    screeningFacts,
}: DogPageSectionsProps & { listing: ListingData }) {
    return (
        <LocationSection
            data={listing}
            onAskKits={copilotEnabled ? requestOpenChat : undefined}
            screeningFacts={screeningFacts}
        />
    );
}

function Faq({
    listing,
    dogId,
    dogName,
    copilotEnabled,
    listingPageV2,
    providerProfile,
}: DogPageSectionsProps & { listing: ListingData }) {
    const posthog = usePostHog();
    const [faqAsk, setFaqAsk] = useState<FaqAsk | null>(null);
    const reply = useListingQuestionReply(faqAsk?.inputId ?? null);
    const ask = (question: string, source: FaqAskSource) => {
        posthog.capture("faq_question_asked", {
            dogId,
            dogName,
            source,
            questionLength: question.length,
        });
        const inputId = requestListingQuestion(
            dogId,
            question,
            `faq_${source}`,
        );
        setFaqAsk({ question, source, inputId, stalled: false });
        setTimeout(
            () =>
                setFaqAsk((current) =>
                    current?.inputId === inputId
                        ? { ...current, stalled: true }
                        : current,
                ),
            FAQ_ASK_STALL_MS,
        );
    };
    const continueAsk = () => {
        posthog.capture("faq_question_continued", {
            dogId,
            source: faqAsk?.source,
            replyState: reply?.state,
        });
        requestOpenChat();
    };
    return (
        <FaqSection
            data={listing}
            inclusions={listingFeeInclusions({
                listingIncluded: listingPageV2?.application?.included,
                providerIncluded: providerProfile?.pricing.fees[0]?.includes,
            })}
            kits={
                copilotEnabled
                    ? {
                          onAsk: ask,
                          onContinue: continueAsk,
                          active:
                              faqAsk && reply
                                  ? { ...faqAsk, reply }
                                  : undefined,
                      }
                    : undefined
            }
        />
    );
}

export function ListingSections(props: DogPageSectionsProps) {
    const { listing } = props;
    if (!listing) {
        return (
            <div className="mt-12 flex flex-col gap-14">
                <Parents {...props} />
                <Reviews {...props} />
            </div>
        );
    }
    const withListing = { ...props, listing };
    return (
        <div className="mt-12 flex flex-col gap-14">
            <div className="flex flex-col gap-6">
                <BreedSection data={listing} />
                <Price {...withListing} />
            </div>
            <Parents {...props} />
            <ProviderBlock {...withListing} />
            <Location {...withListing} />
            <Faq key={props.dogId} {...withListing} />
            <Reviews {...props} />
        </div>
    );
}
